import type { Row } from "@rocicorp/zero";
import { styled } from "goober";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { queries } from "../../shared/queries";
import { useAdminLandingView } from "../hooks/useAdminLandingView";
import { useAdminSpaceView } from "../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../hooks/useAdminViewSubject";
import { navEnterStyles, useNavTransition } from "../hooks/useNavTransition";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { useMoveWorkflow } from "../hooks/useMoveWorkflow";
import { useQuery } from "../hooks/useQuery";
import { useScrollFade } from "../hooks/useScrollFade";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { branchLastActivity } from "../lib/branch";
import { playClick } from "../lib/sounds";
import { spaceIcon, spaceIconTooltip } from "../lib/spaceIcon";
import { makeUrl, navigate, routes, useRouteMatch } from "../lib/urls";
import { AdminNavigation } from "./AdminNavigation";
import { DiscoverableSpaceIcon } from "./DiscoverableSpaceIcon";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { InfoIcon } from "./InfoIcon";
import {
  applyVisibilityRule,
  type NavConfigEntry,
  type NavItemKey,
  resolveNavConfig,
  sectionEntries,
} from "./navItems";
import { JoinSpaceModal } from "./JoinSpaceModal";
import { Link } from "./Link";
import { MoveWorkflowModal } from "./MoveWorkflowModal";
import { NavContextMenu } from "./NavContextMenu";
import { PressButton } from "./PressButton";
import { RecentlyDeletedWorkflowsModal } from "./RecentlyDeletedWorkflowsModal";
import { SpaceActionsMenu } from "./SpaceActionsMenu";
import { SpaceSettings } from "./SpaceSettings";
import { SubtleButton } from "./SubtleButton";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

export const Container = styled("div")`
  width: 228px;
  padding: 12px 12px 0;
  overflow: auto;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  ${navEnterStyles}
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
`;

const linkStyles = `
  --item-bg: var(--surface-primary);
  --item-active-bg: linear-gradient(var(--alpha4)), var(--surface-primary);
  padding: 8px;
  display: flex;
  border-radius: 8px;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  color: var(--text-primary);
  gap: 8px;
  &:hover {
    background-color: var(--alpha4) !important;
    --item-bg: var(--item-active-bg);
  }
`;

export const StyledLink = styled(Link)`
  ${linkStyles}
`;

export const Heading = styled("h4")`
  padding: 10px 8px 10px 8px;
  padding-right: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Break = styled("hr")`
  border: none;
  margin: 8px;
  height: 0.5px;
  background-color: var(--border-alpha);
`;

export const SpaceItemContainer = styled("div")`
  position: relative;
  display: flex;
  align-items: center;

  & [data-space-action] {
    opacity: 0;
    pointer-events: none;
  }

  &:hover [data-space-action] {
    opacity: 1;
    pointer-events: auto;
  }
`;

const LabelWithInfo = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export function NavInfo({ children }: { children: ReactNode }) {
  return (
    <Tooltip
      content={
        <div style={{ width: 200 }}>
          <Text variant="text-small-md">{children}</Text>
        </div>
      }
    >
      <InfoIcon>
        <Icon name="info" size={12} />
      </InfoIcon>
    </Tooltip>
  );
}

const SidebarButtonElement = styled(PressButton)`
  ${linkStyles}
  width: 100%;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
  &[data-muted="true"] {
    color: var(--text-secondary);
    opacity: 0.6;
  }
  &[data-muted="true"]:hover {
    opacity: 0.85;
  }
`;

// `data-no-pill` opts out of the global button-pill rule (global.css) so the 8px from linkStyles applies, matching the link rows.
function SidebarButton({
  muted,
  ...props
}: ComponentProps<typeof SidebarButtonElement> & { muted?: boolean }) {
  return <SidebarButtonElement {...props} data-no-pill="" data-muted={muted} />;
}

let sidebarTogglePreviewHovered = false;
let sidebarTogglePreviewSuppressed = false;
const sidebarTogglePreviewListeners = new Set<() => void>();
function notifySidebarTogglePreview() {
  for (const listener of sidebarTogglePreviewListeners) listener();
}
function sidebarTogglePreviewActive() {
  return sidebarTogglePreviewHovered && !sidebarTogglePreviewSuppressed;
}
function setSidebarTogglePreviewHovered(value: boolean) {
  if (sidebarTogglePreviewHovered === value) return;
  sidebarTogglePreviewHovered = value;
  notifySidebarTogglePreview();
}
function suppressSidebarTogglePreview() {
  if (sidebarTogglePreviewSuppressed) return;
  sidebarTogglePreviewSuppressed = true;
  notifySidebarTogglePreview();
}
function clearSidebarTogglePreviewSuppression() {
  if (!sidebarTogglePreviewSuppressed) return;
  sidebarTogglePreviewSuppressed = false;
  notifySidebarTogglePreview();
}
function useSidebarTogglePreviewActive() {
  return useSyncExternalStore(
    (callback) => {
      sidebarTogglePreviewListeners.add(callback);
      return () => sidebarTogglePreviewListeners.delete(callback);
    },
    sidebarTogglePreviewActive,
    () => false
  );
}
function useSidebarTogglePreviewSuppressed() {
  return useSyncExternalStore(
    (callback) => {
      sidebarTogglePreviewListeners.add(callback);
      return () => sidebarTogglePreviewListeners.delete(callback);
    },
    () => sidebarTogglePreviewSuppressed,
    () => false
  );
}

const SidebarToggleButton = styled("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:active:not(:disabled) {
    background: var(--alpha8);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  & svg {
    opacity: 0.25;
  }
  & .inner {
    transition: width 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-collapsed="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="false"][data-preview="true"] .inner {
    width: 1px;
  }
  &[data-collapsed="true"][data-preview="true"] .inner {
    width: 4px;
  }
`;

export const Badge = styled("span")`
  position: relative;
  background: var(--alpha4);
  color: var(--text-secondary);
  font-size: 9px;
  font-weight: 500;
  line-height: 12px;
  display: inline-flex;
  min-width: 16px;
  width: fit-content;
  border-radius: 10px;
  padding: 2px 4px;
  justify-content: center;
  margin-left: auto;

  &[data-hidden="true"] {
    opacity: 0;
  }
`;

const BadgeDotContainer = styled("div")`
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--item-bg);
`;

const BadgeDot = styled("div")`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--orange-600);
`;

type PatternInput = NonNullable<Parameters<typeof useRouteMatch>[0]>;

type ItemProps<T extends PatternInput> = {
  scope: T;
  children: ReactNode;
};

export function Item<T extends PatternInput>({
  scope,
  children,
}: ItemProps<T>) {
  const [match] = useRouteMatch(scope);
  const href = makeUrl(scope);

  return (
    <StyledLink
      to={href}
      style={
        match
          ? ({
              backgroundColor: "var(--alpha4)",
              "--item-bg": "var(--item-active-bg)",
            } as React.CSSProperties)
          : { backgroundColor: "transparent" }
      }
    >
      {children ?? ""}
    </StyledLink>
  );
}

function PersonalItem({ spaceId }: { spaceId: string }) {
  const isMobile = useIsMobile();
  const [match] = useRouteMatch(routes.personal);
  const [hover, setHover] = useState(false);
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  const href = makeUrl(routes.personal);

  const dropdownSections = [
    {
      items: [
        {
          type: "button" as const,
          left: <Icon name="box" size={16} />,
          label: "Browse storage",
          onClick: () =>
            navigate(routes.modal.storage, { spaceId, section: "workflows" }),
        },
        {
          type: "button" as const,
          left: <Icon name="package-2" size={16} />,
          label: "Recently deleted",
          onClick: () => setRecentlyDeletedOpen(true),
        },
      ],
    },
  ];

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={href}
        style={{
          backgroundColor: match || hover ? "var(--alpha4)" : "transparent",
          flex: 1,
          paddingRight: 32,
          overflow: "hidden",
        }}
      >
        <Icon name="circle-user-round" size={16} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>Personal workflows</Text>
        </span>
      </StyledLink>
      <div
        data-space-action
        style={{
          position: "absolute",
          right: 4,
          opacity: isMobile ? 1 : undefined,
          pointerEvents: isMobile ? "auto" : undefined,
        }}
      >
        <Dropdown
          opener={<SubtleButton icon="more" variant="secondary" />}
          align="right"
          minWidth={160}
          sections={dropdownSections}
        />
      </div>
      <RecentlyDeletedWorkflowsModal
        spaceId={spaceId}
        open={recentlyDeletedOpen}
        onClose={() => setRecentlyDeletedOpen(false)}
      />
    </SpaceItemContainer>
  );
}

function OwnChatItem({
  chatTabs,
  showDot,
  personalSpaceId,
}: {
  chatTabs: readonly { id: string }[];
  showDot: boolean;
  personalSpaceId: string | null;
}) {
  const isMobile = useIsMobile();
  const [match, params] = useRouteMatch(routes.chats);
  const isOwnChats = match && !(params as { userId?: string }).userId;
  const [hover, setHover] = useState(false);
  const showAction = personalSpaceId != null && (hover || isMobile);

  const dropdownSections = [
    {
      items: [
        {
          type: "button" as const,
          left: <Icon name="history" size={16} />,
          label: "History",
          onClick: () => navigate(routes.chats, { userId: "", history: "1" }),
        },
        {
          type: "button" as const,
          left: <Icon name="box" size={16} />,
          label: "Browse storage",
          onClick: () =>
            personalSpaceId &&
            navigate(routes.modal.storage, {
              spaceId: personalSpaceId,
              section: "chats",
            }),
        },
      ],
    },
  ];

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={makeUrl(routes.chats, { userId: "" })}
        style={
          {
            backgroundColor:
              isOwnChats || hover ? "var(--alpha4)" : "transparent",
            flex: 1,
            overflow: "hidden",
            ...(isOwnChats ? { "--item-bg": "var(--item-active-bg)" } : {}),
          } as React.CSSProperties
        }
      >
        <Icon name="ask" size={16} />
        <LabelWithInfo>
          <Text>Chat</Text>
          <NavInfo>
            Chat with 3B for quick tasks and questions, using all your
            connectors
          </NavInfo>
        </LabelWithInfo>
        {chatTabs.length > 0 ? (
          <Badge data-hidden={showAction}>
            {chatTabs.length}
            {showDot && (
              <BadgeDotContainer>
                <BadgeDot />
              </BadgeDotContainer>
            )}
          </Badge>
        ) : null}
      </StyledLink>
      {personalSpaceId ? (
        <div
          style={{
            position: "absolute",
            right: 4,
            opacity: showAction ? 1 : 0,
            pointerEvents: showAction ? "auto" : "none",
          }}
        >
          <Dropdown
            opener={<SubtleButton icon="more" variant="secondary" />}
            align="right"
            minWidth={160}
            sections={dropdownSections}
          />
        </div>
      ) : null}
    </SpaceItemContainer>
  );
}

function DiscoverableSpaceItem({ space }: { space: Row["spaces"] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const displayName = useSpaceDisplayName();
  const [hover, setHover] = useState(false);

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <SidebarButton
        muted
        onPress={() => setModalOpen(true)}
        style={{
          backgroundColor: hover ? "var(--alpha4)" : "transparent",
        }}
      >
        <Tooltip content="Discoverable space, click to join">
          <DiscoverableSpaceIcon />
        </Tooltip>
        <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Text ellipsis>{displayName(space)}</Text>
        </span>
      </SidebarButton>
      <JoinSpaceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        space={space}
      />
    </SpaceItemContainer>
  );
}

function SpaceItem({
  space,
  viewOnly,
}: {
  space: Row["spaces"];
  viewOnly?: boolean;
}) {
  const isMobile = useIsMobile();
  const [match] = useRouteMatch(routes.space, { spaceId: space.id });
  const [hover, setHover] = useState(false);
  const href = makeUrl(routes.space, { spaceId: space.id });
  const displayName = useSpaceDisplayName();

  const icon = <Icon name={spaceIcon(space.mode)} size={16} />;
  const iconTooltip = spaceIconTooltip(space.mode);

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={href}
        style={{
          backgroundColor: match || hover ? "var(--alpha4)" : "transparent",
          flex: 1,
          paddingRight: 32,
          overflow: "hidden",
          ...(viewOnly && {
            color: "var(--text-secondary)",
            opacity: hover ? 0.85 : 0.6,
          }),
        }}
      >
        {iconTooltip ? <Tooltip content={iconTooltip}>{icon}</Tooltip> : icon}
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>{displayName(space)}</Text>
        </span>
      </StyledLink>
      {viewOnly ? (
        <div
          style={{
            position: "absolute",
            right: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
          }}
        >
          <Tooltip content="Viewing as admin, not a member">
            <span
              style={{ display: "inline-flex", color: "var(--text-secondary)" }}
            >
              <Icon name="eye" size={12} />
            </span>
          </Tooltip>
        </div>
      ) : (
        <div
          data-space-action
          style={{
            position: "absolute",
            right: 4,
            opacity: isMobile ? 1 : undefined,
            pointerEvents: isMobile ? "auto" : undefined,
          }}
        >
          <SpaceActionsMenu space={space} />
        </div>
      )}
    </SpaceItemContainer>
  );
}

export function Navigation() {
  const subjectUserId = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const adminLanding = useAdminLandingView();
  if (subjectUserId || adminSpace || adminLanding) {
    return <AdminNavigation subjectUserId={subjectUserId} />;
  }
  return <WorkspaceNavigation />;
}

function WorkspaceNavigation() {
  const navEnter = useNavTransition("workspace");
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  const isMobile = useIsMobile();
  const isNarrow = useIsNarrow();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();
  const sidebarTogglePreview = useSidebarTogglePreviewActive();
  const sidebarTogglePreviewClickSuppressed =
    useSidebarTogglePreviewSuppressed();
  const [readableSpaces] = useSpaces({ settingsView: false });
  const nonPersonalSpaces = readableSpaces.filter((s) => s.mode !== "personal");
  const memberSpaces = nonPersonalSpaces.filter(isSpaceMember);
  const discoverableSpaces = nonPersonalSpaces.filter((s) => !isSpaceMember(s));
  const [navBranches] = useQuery(queries.navBranches());
  const [chatTabs] = useQuery(queries.personalChatTabs());
  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const canCreateSpace = useHasAccess("tenant:space_create");

  const [, spaceRouteParams] = useRouteMatch(routes.space);
  const activeSpaceId = (spaceRouteParams as { spaceId?: string }).spaceId;
  const memberSpaceIds = new Set(memberSpaces.map((s) => s.id));
  const [activeSpaceResult] = useQuery(
    queries.navSpace({
      spaceId: activeSpaceId ?? "",
    })
  );
  const activeSpace =
    activeSpaceId &&
    !memberSpaceIds.has(activeSpaceId) &&
    activeSpaceId !== personalSpaceId
      ? activeSpaceResult
      : null;
  const presenceMap = new Map(
    workflowViews.map((v) => [v.workflowId, v.lastSeenAt])
  );
  const showBranchDot = navBranches.some((branch) => {
    const workflow = branch.workflow;
    if (!workflow) return false;
    const lastSeenAt = presenceMap.get(workflow.id);
    const lastActivity = branchLastActivity(branch);
    const hasSeen = lastSeenAt != null && lastSeenAt >= lastActivity;
    if (hasSeen) return false;
    if (branch.awaitingAction) return true;
    if (workflow.autofixBranchId === branch.id) return true;
    if (workflow.autoImproveBranchId === branch.id) return true;
    return false;
  });
  const showChatDot = chatTabs.some((t) => t.chat?.awaitingAction);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [navMenu, setNavMenu] = useState<{ x: number; y: number } | null>(null);

  const [pendingMoveWorkflowId, setPendingMoveWorkflowId] = useState<
    string | null
  >(null);

  const {
    requestMove,
    pending: movePending,
    confirmMove,
    cancelMove,
  } = useMoveWorkflow();

  const canCreateSpaceRef = useRef(canCreateSpace);
  canCreateSpaceRef.current = canCreateSpace;

  useEffect(() => {
    const open = (e: Event) => {
      if (!canCreateSpaceRef.current) return;
      const workflowId = (e as CustomEvent<string>).detail;
      if (workflowId) setPendingMoveWorkflowId(workflowId);
      setCreateModalOpen(true);
    };
    document.addEventListener("create-space", open);
    return () => document.removeEventListener("create-space", open);
  }, []);

  const handleSpaceCreated = (spaceId: string, name: string) => {
    if (pendingMoveWorkflowId) {
      requestMove({
        workflowId: pendingMoveWorkflowId,
        targetSpaceId: spaceId,
        targetSpaceName: name,
      });
      setPendingMoveWorkflowId(null);
    }
    setCreateModalOpen(false);
    navigate(routes.space, { spaceId });
  };

  const [preferences] = useQuery(queries.userPreferences());
  const navConfig = resolveNavConfig(preferences?.navConfig);
  const navBadged: Partial<Record<NavItemKey, boolean>> = {
    branches: showBranchDot,
    chat: showChatDot,
  };
  const isNavItemVisible = ({ item, rule }: NavConfigEntry) => {
    if (item.desktopOnly && isMobile) return false;
    if (item.key === "personal" && !personalSpaceId) return false;
    return applyVisibilityRule(rule, navBadged[item.key] ?? false);
  };
  const renderNavItem = ({ item, rule }: NavConfigEntry) => {
    if (!isNavItemVisible({ item, rule })) return null;
    switch (item.key) {
      case "chat":
        return (
          <OwnChatItem
            key="chat"
            chatTabs={chatTabs}
            showDot={showChatDot}
            personalSpaceId={personalSpaceId}
          />
        );
      case "personal":
        return personalSpaceId ? (
          <PersonalItem key="personal" spaceId={personalSpaceId} />
        ) : null;
      case "branches":
        return (
          <Item key="branches" scope={routes.branches}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
            {navBranches.length > 0 && (
              <Badge>
                {navBranches.length}
                {showBranchDot && (
                  <BadgeDotContainer>
                    <BadgeDot />
                  </BadgeDotContainer>
                )}
              </Badge>
            )}
          </Item>
        );
      case "recents":
        return (
          <Item key="recents" scope={routes.recents}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
          </Item>
        );
      case "favorites":
        return (
          <Item key="favorites" scope={routes.favorites}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
          </Item>
        );
      case "connectors":
        return (
          <Item key="connectors" scope={routes.connectors}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
          </Item>
        );
      case "skills":
        return (
          <Item key="skills" scope={routes.skills}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
          </Item>
        );
      case "monitoring":
        return (
          <Item key="monitoring" scope={routes.monitoring}>
            <Icon name={item.icon} size={16} />
            <Text>{item.label}</Text>
          </Item>
        );
    }
  };

  const mainEntries = sectionEntries(navConfig, "main");
  const manageEntries = sectionEntries(navConfig, "manage");
  const mainVisible = mainEntries.some(isNavItemVisible);
  const manageVisible = manageEntries.some(isNavItemVisible);

  return (
    <Container
      ref={scrollRef}
      data-nav-enter={navEnter}
      onContextMenu={(e) => {
        // Only the customize menu on empty nav space; interactive rows (space
        // links, nav items, buttons) keep their default right-click behavior.
        if ((e.target as HTMLElement).closest("a, button, [role='button']"))
          return;
        e.preventDefault();
        setNavMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      {mainEntries.map(renderNavItem)}

      {mainVisible && manageVisible && <Break />}

      {manageEntries.map(renderNavItem)}

      {(mainVisible || manageVisible) && <Break />}

      <Heading>
        <Text variant="text-small">Spaces</Text>
        <div style={{ display: "flex", gap: 2 }}>
          <Tooltip
            content={
              !canCreateSpace
                ? "You don't have permission to create spaces"
                : "Create space"
            }
          >
            <SubtleButton
              icon="plus"
              variant="secondary"
              disabled={!canCreateSpace}
              onClick={() => setCreateModalOpen(true)}
            />
          </Tooltip>
        </div>
      </Heading>

      {[...memberSpaces]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((space) => (
          <SpaceItem key={space.id} space={space} />
        ))}
      {activeSpace && (
        <SpaceItem key={activeSpace.id} space={activeSpace} viewOnly />
      )}
      {discoverableSpaces.length > 0 &&
        [...discoverableSpaces]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((space) => (
            <DiscoverableSpaceItem key={space.id} space={space} />
          ))}

      <div style={{ flex: 1 }} />

      {!isMobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {!isNarrow && (
            <Tooltip
              hidden={sidebarTogglePreviewClickSuppressed}
              content={
                <>
                  {sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}{" "}
                  <span
                    style={{ fontSize: 10, color: "var(--text-secondary)" }}
                  >
                    ⌘\
                  </span>
                </>
              }
            >
              <SidebarToggleButton
                type="button"
                data-collapsed={sidebarCollapsed}
                data-preview={sidebarTogglePreview}
                onMouseEnter={() => setSidebarTogglePreviewHovered(true)}
                onMouseLeave={() => {
                  setSidebarTogglePreviewHovered(false);
                  clearSidebarTogglePreviewSuppression();
                }}
                onMouseDown={() => {
                  playClick();
                  suppressSidebarTogglePreview();
                  setSidebarCollapsed(!sidebarCollapsed);
                }}
                onClick={(e) => {
                  if (e.detail !== 0) return;
                  suppressSidebarTogglePreview();
                  setSidebarCollapsed(!sidebarCollapsed);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M11 2C12.6569 2 14 3.34315 14 5L14 11C14 12.6569 12.6569 14 11 14L6 14L5 14C3.34315 14 2 12.6569 2 11L2 5C2 3.34315 3.34315 2 5 2L6 2L11 2Z"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect
                    className="inner"
                    x="4"
                    y="4"
                    width="4"
                    height="8"
                    rx="1.5"
                    fill="currentColor"
                  />
                </svg>
              </SidebarToggleButton>
            </Tooltip>
          )}
        </div>
      )}

      {createModalOpen && (
        <SpaceSettings
          mode="create"
          onClose={() => {
            setCreateModalOpen(false);
            setPendingMoveWorkflowId(null);
          }}
          onCreated={handleSpaceCreated}
        />
      )}

      {movePending && (
        <MoveWorkflowModal
          workflowId={movePending.workflowId}
          targetSpaceId={movePending.targetSpaceId}
          targetSpaceName={movePending.targetSpaceName}
          onClose={cancelMove}
          onConfirm={confirmMove}
        />
      )}

      {navMenu && (
        <NavContextMenu
          x={navMenu.x}
          y={navMenu.y}
          onClose={() => setNavMenu(null)}
        />
      )}
    </Container>
  );
}
